import React from 'react';
import { Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import '../css/StaticPages.css';

const Guide = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.documentElement.classList.add('allow-page-scroll');
    document.body.classList.add('allow-page-scroll');
    return () => {
      document.documentElement.classList.remove('allow-page-scroll');
      document.body.classList.remove('allow-page-scroll');
    };
  }, []);

  return (
    <div style={{ maxWidth: '900px', margin: '40px auto', padding: '0 16px', lineHeight: 1.75 }}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/')}
        style={{ padding: 0, marginBottom: 16 }}
      >
        Back to Dashboard
      </Button>

      <h1 style={{ marginBottom: 8 }}>VNotes AI — Product Guide</h1>

      {/* Table of Contents */}
      <div style={{ background: '#f9f9f9', padding: '16px 24px', borderRadius: 8, marginBottom: 32 }}>
        <h3 style={{ marginTop: 0 }}>Table of Contents</h3>
        <ol style={{ marginBottom: 0 }}>
          <li><a href="#getting-started">Getting Started</a></li>
          <li><a href="#dashboard">The Dashboard</a></li>
          <li><a href="#creating-notes">Creating Notes</a></li>
          <li><a href="#organizing">Organizing Your Knowledge</a></li>
          <li><a href="#searching">Searching Your Knowledge Base</a></li>
          <li><a href="#ai-tools">AI Tools</a></li>
          <li><a href="#ai-doc-writer">AI Doc Writer</a></li>
          <li><a href="#collaborative">Collaborative Knowledge Bases</a></li>
          <li><a href="#membership">Membership &amp; Billing</a></li>
          <li><a href="#account">Account Settings</a></li>
          <li><a href="#chrome-extension">Chrome Extension — VNotes AI Clip</a></li>
          <li><a href="#need-help">Need Help?</a></li>
        </ol>
      </div>

      {/* 1. Getting Started */}
      <h2 id="getting-started">1. Getting Started</h2>

      <h3>Creating an Account</h3>
      <ol>
        <li>Visit <strong>vnotes.ai</strong>, open <strong>Web version</strong> and click <strong>Sign Up</strong>.</li>
        <li>Enter your <strong>Name</strong>, <strong>Email</strong>, and <strong>Password</strong>.</li>
        <li>Alternatively, click <strong>Sign in with Google</strong> to register with your Google account.</li>
        <li>If email verification is enabled, check your inbox and click the verification link to activate your account.</li>
        <li>Once verified, sign in with your credentials.</li>
      </ol>

      <h3>Signing In</h3>
      <ol>
        <li>Enter your <strong>Email</strong> and <strong>Password</strong>.</li>
        <li>Check <strong>Remember Me</strong> to stay signed in across sessions.</li>
        <li>Click <strong>Login</strong>.</li>
        <li>If you've forgotten your password, click <strong>Forgot Password</strong> to receive a reset link by email.</li>
      </ol>

      {/* 2. The Dashboard */}
      <h2 id="dashboard">2. The Dashboard</h2>
      <p>After signing in, you land on the <strong>Dashboard</strong> — your central workspace. It has three panels:</p>

      <pre style={{
        background: '#f5f5f5',
        padding: '16px',
        borderRadius: 8,
        overflowX: 'auto',
        fontSize: '13px',
        lineHeight: 1.5
      }}>
{`┌────────────────────────────────────────────────────────────┐
│  Header: Logo  |  Search Bar  |  User Menu                │
├─────────────────┬──────────────────┬───────────────────────┤
│  Left Sidebar   │  Note List       │  Note Detail /        │
│                 │                  │  AI Tools             │
│  Quick Actions  │  Cards with      │                       │
│  Personal       │  title, summary, │  View, edit, and      │
│  Library        │  tags, date      │  interact with the    │
│  Team Libraries │                  │  selected note        │
└─────────────────┴──────────────────┴───────────────────────┘`}
      </pre>

      <h3>Left Sidebar</h3>
      <p><strong>Quick Actions</strong> at the top provide fast navigation:</p>
      <ul>
        <li><strong>All Notes</strong> — View every note across all knowledge bases.</li>
        <li><strong>Favorites</strong> — View notes you've pinned as favorites.</li>
        <li><strong>Recent (7 days)</strong> — View notes created or updated in the last week.</li>
      </ul>
      <p>Below that, the sidebar shows two sections:</p>
      <ul>
        <li><strong>Personal Library</strong> — Your private knowledge base with a hierarchical topic tree.</li>
        <li><strong>Team Libraries</strong> — Collaborative knowledge bases shared with others.</li>
      </ul>
      <p>Click any topic or knowledge base to filter the note list in the center panel.</p>

      <h3>Center Panel — Note List</h3>
      <p>The center panel displays notes as cards. Each card shows:</p>
      <ul>
        <li>Note title</li>
        <li>Summary (truncated, with expand option)</li>
        <li>Tags (color-coded, up to 3 visible)</li>
        <li>Creation date</li>
        <li>Favorite indicator (star icon)</li>
      </ul>
      <p>Use the search bar at the top to search within the current view. Toggle between search modes by clicking the mode indicator:</p>
      <ul>
        <li><strong>Hybrid Search</strong> — Combines keyword matching with semantic similarity.</li>
        <li><strong>Ask</strong> — Ask a natural language question across your notes and get an AI-generated answer.</li>
      </ul>
      <p>Click <strong>Load More</strong> at the bottom to paginate through results.</p>

      <h3>Right Panel — Note Detail</h3>
      <p>Click any note card to open it in the right panel, where you can:</p>
      <ul>
        <li>Read the full content (rendered Markdown)</li>
        <li>View metadata: creation date, last updated, author</li>
        <li>See topic assignments and tags</li>
        <li>Expand or collapse long content sections</li>
      </ul>

      {/* 3. Creating Notes */}
      <h2 id="creating-notes">3. Creating Notes</h2>
      <p>Click the <strong>New Note</strong> button in the left sidebar. A creation modal opens with two tabs:</p>

      <h3>Manual Input</h3>
      <ul>
        <li><strong>Title</strong> (required) — Give your note a descriptive name.</li>
        <li><strong>Knowledge Library</strong> (required) — Choose Personal Library or a Team Library from the dropdown.</li>
        <li><strong>Content</strong> (required) — Write or paste content into the Markdown editor. Supports rich formatting, code blocks, and more.</li>
      </ul>

      <h3>File Upload</h3>
      <p>Upload documents directly. Supported formats:</p>
      <ul>
        <li><strong>PDF</strong> — Extracted and converted to Markdown.</li>
        <li><strong>DOCX / DOC</strong> — Word documents parsed and imported.</li>
        <li><strong>PPTX</strong> — PowerPoint presentations extracted.</li>
        <li><strong>XLSX</strong> — Spreadsheets imported.</li>
        <li><strong>Markdown / Text</strong> — Imported as-is.</li>
      </ul>

      <h3>What Happens After Creation</h3>
      <p>Once you save, VNotes' AI pipeline processes your note in the background:</p>
      <ol>
        <li><strong>Summarization</strong> — An AI-generated summary is created automatically.</li>
        <li><strong>Tagging</strong> — Relevant keywords are extracted and applied as tags.</li>
        <li><strong>Topic Assignment</strong> — The note is matched to the most relevant topic in your knowledge base.</li>
        <li><strong>Embedding</strong> — A vector embedding is generated for semantic search.</li>
      </ol>
      <p>You'll see a success message confirming how many smart tags were created.</p>

      {/* 4. Organizing Your Knowledge */}
      <h2 id="organizing">4. Organizing Your Knowledge</h2>

      <h3>Topics</h3>
      <p>Topics provide a two-level hierarchy within each knowledge base.</p>

      <p><strong>Creating a topic:</strong></p>
      <ol>
        <li>Click the <strong>New Topic</strong> button next to your Personal Library or Team Library header.</li>
        <li>Enter a <strong>Topic Name</strong> (required).</li>
        <li>Enter a <strong>Description</strong> (required, minimum 20 characters) — helps the AI with automatic topic matching.</li>
        <li>Optionally add a <strong>Tag List</strong> (comma-separated) or click <strong>AI Generate</strong> to let the AI suggest tags based on the description.</li>
        <li>Click <strong>Create</strong>.</li>
      </ol>

      <p><strong>Creating a subtopic:</strong></p>
      <ul>
        <li>Click the dropdown menu (<strong>...</strong>) on any topic and select <strong>New Topic</strong> to create a child topic underneath it.</li>
      </ul>

      <p><strong>Editing a topic:</strong></p>
      <ul>
        <li>Click the dropdown menu on a topic and select <strong>Rename</strong> to update the name, description, or tags.</li>
      </ul>

      <h3>Tags</h3>
      <p>Tags are color-coded labels on your notes.</p>

      <p><strong>Adding tags to a note:</strong></p>
      <ol>
        <li>Open a note in the right panel.</li>
        <li>In the Tags section, click <strong>Add Tag</strong>.</li>
        <li>Type a tag name or select from your <strong>Recent 30 tags</strong> shown below the input for quick access.</li>
      </ol>
      <p>Tags are limited to 50 characters each.</p>

      <p><strong>Filtering by tag:</strong></p>
      <ul>
        <li>Click any tag on a note card to filter the note list to all notes with that tag.</li>
      </ul>

      <h3>Pinning Favorites</h3>
      <p>Click the star icon on any note to pin it as a favorite. Pinned notes appear in the <strong>Favorites</strong> quick action view and are marked with a filled star.</p>

      <h3>Note Actions</h3>
      <p>Open the more actions menu (three-dot icon) on a note detail card for additional options:</p>
      <ul>
        <li><strong>Refresh Note</strong> — Re-extract content from the original source URL.</li>
        <li><strong>Run AI Analysis</strong> — Re-run the full AI pipeline (summarization, tagging, topic assignment). This runs in the background — you can safely close the dialog and continue working.</li>
        <li><strong>Copy to Team</strong> — Copy this note into a collaborative knowledge base.</li>
        <li><strong>Copy to Personal</strong> — Copy a collaborative note into your personal library.</li>
      </ul>

      {/* 5. Searching Your Knowledge Base */}
      <h2 id="searching">5. Searching Your Knowledge Base</h2>
      <p>VNotes offers multiple ways to find information:</p>

      <h3>Dashboard Search</h3>
      <p>Use the search bar at the top of the note list. Three modes are available:</p>
      <ul>
        <li><strong>Hybrid Search</strong> — Best for most queries. Combines keyword matching with semantic vector similarity for the most relevant results.</li>
        <li><strong>Fulltext Search</strong> — Searches for exact keyword matches. Useful when you know the specific terms used.</li>
        <li><strong>Ask</strong> — Enter a natural language question. VNotes retrieves relevant notes, synthesizes an answer, and shows the source references with confidence scores.</li>
      </ul>

      <h3>Search Page</h3>
      <p>For a dedicated search experience, navigate to the Search page from the header. Features include:</p>
      <ul>
        <li>Toggle between <strong>Text Search</strong> and <strong>Semantic Search</strong>.</li>
        <li>View similarity scores (shown as percentages) when using semantic search.</li>
        <li>Browse recent search history — click any previous search term to re-run it.</li>
        <li>Paginated results with configurable page size.</li>
      </ul>

      <h3>Ask Mode (Multi-Note Q&amp;A)</h3>
      <p>When you switch to Ask mode in the dashboard, you can ask questions across all notes in the selected library or topic. The AI:</p>
      <ol>
        <li>Searches your knowledge base for relevant content.</li>
        <li>Synthesizes an answer grounded in your notes.</li>
        <li>Shows which notes were used as sources.</li>
        <li>Displays relevance scores for each source.</li>
      </ol>
      <p>You can have a multi-turn conversation — the AI remembers context from previous questions in the session (up to 10 question-answer pairs).</p>

      {/* 6. AI Tools */}
      <h2 id="ai-tools">6. AI Tools</h2>
      <p>Select any note in the dashboard, then scroll to the <strong>AI Tools</strong> section at the bottom of the note detail panel. Four tools are available:</p>

      <h3>Chat with AI</h3>
      <p>An interactive conversation interface for the selected note.</p>
      <p><strong>How to use:</strong></p>
      <ol>
        <li>Click <strong>Chat with AI</strong> from the tool selector.</li>
        <li>Type a question in the input field, or try one of the suggested prompts:
          <ul>
            <li>"What are the main points?"</li>
            <li>"Summarize the key ideas"</li>
            <li>"Are there any action items?"</li>
            <li>"Explain this in simpler terms"</li>
          </ul>
        </li>
        <li>The AI responds with answers grounded in the note's content, with source attribution.</li>
        <li>Continue the conversation — ask follow-up questions or dig deeper.</li>
      </ol>
      <p>Chat history is preserved per note and saved to your browser. Switch notes and come back without losing context.</p>

      <h3>AI Quiz</h3>
      <p>Test your understanding of any saved note.</p>
      <p><strong>How to use:</strong></p>
      <ol>
        <li>Click <strong>AI Quiz</strong> from the tool selector.</li>
        <li>Click <strong>Generate Question</strong> to start.</li>
        <li>Read the question and type your answer in the text area.</li>
        <li>Click <strong>Submit Answer</strong>.</li>
        <li>See your result:
          <ul>
            <li><strong>Correct</strong> — shown in green with feedback.</li>
            <li><strong>Incorrect</strong> — shown in red with the correct answer and a quote from the source note.</li>
          </ul>
        </li>
        <li>Click <strong>Next Question</strong> to continue.</li>
      </ol>
      <p>When all questions are exhausted, view your <strong>Quiz Results</strong>:</p>
      <ul>
        <li>Total questions, correct, incorrect, and accuracy percentage.</li>
        <li>Full Q&amp;A summary with color-coded history.</li>
        <li>Click <strong>Start New Quiz</strong> to retake.</li>
      </ul>

      <h3>Ideate &amp; Brainstorm</h3>
      <p>Expand ideas using AI and your knowledge library as context.</p>

      <p><strong>Stage 1 — Define Task:</strong></p>
      <ol>
        <li>Click <strong>Ideate &amp; Brainstorm</strong> from the tool selector.</li>
        <li>Describe what you want to brainstorm in the input field.</li>
        <li>The AI will analyze your task and respond with:
          <ul>
            <li>A <strong>Task Summary</strong> — a rewritten, clarified version of your problem.</li>
            <li><strong>Clarifying Questions</strong> — each with an explanation of why it matters.</li>
            <li>A readiness indicator: green ("Ready to brainstorm!") or amber ("Needs clarification").</li>
          </ul>
        </li>
        <li>Answer the clarifying questions or refine your task description.</li>
        <li>When ready, click <strong>Start Brainstorm</strong>.</li>
      </ol>

      <p><strong>Stage 2 — Brainstorming:</strong></p>
      <ul>
        <li>The AI generates ideas grounded in your knowledge library.</li>
        <li>Continue the conversation — ask follow-up questions or explore specific ideas.</li>
        <li>Use the library selector at the bottom to choose which knowledge bases to include as context for richer, more relevant ideas.</li>
        <li>Click <strong>Clear History</strong> to start a fresh brainstorming session.</li>
      </ul>

      <h3>AI Doc Writer</h3>
      <p>Generate full documents from your knowledge base. See the dedicated section below.</p>

      {/* 7. AI Doc Writer */}
      <h2 id="ai-doc-writer">7. AI Doc Writer</h2>
      <p>The AI Doc Writer produces structured documents through a guided multi-step pipeline.</p>

      <h3>Starting a Document</h3>
      <ol>
        <li>Click <strong>AI Doc Writer</strong> from the AI Tools section on any note.</li>
        <li>The panel shows a step indicator: <strong>1 Intent → 2 Brief → 3 TOC</strong></li>
      </ol>

      <h3>Step 1 — Intent</h3>
      <p>Select a document type from the grid:</p>
      <ul>
        <li><strong>Product Brief</strong> — Problem, solution, architecture &amp; use cases</li>
        <li><strong>Tutorial</strong> — Step-by-step guide with prerequisites</li>
        <li><strong>Reference</strong> — Scannable API or concept reference</li>
        <li><strong>Technical Report</strong> — Findings, methodology &amp; recommendations</li>
        <li><strong>Explainer</strong> — Concept overview — why it matters</li>
        <li><strong>Runbook</strong> — Operational procedure with rollback</li>
        <li><strong>Comparison</strong> — Options evaluated against criteria</li>
      </ul>
      <p>Describe the main topic and core ideas of the document.</p>
      <p>The AI identifies relevant notes in your library as source material.</p>
      <p>Click <strong>Generate Brief</strong> to proceed.</p>

      <h3>Step 2 — Brief</h3>
      <ul>
        <li>The AI generates a <strong>Writing Brief</strong> — a structured summary of what will be written.</li>
        <li>Review the brief. You can send follow-up messages to refine it.</li>
        <li>When satisfied, click <strong>Create TOC</strong>.</li>
      </ul>

      <h3>Step 3 — Table of Contents</h3>
      <ul>
        <li>The AI generates a <strong>Table of Contents</strong> with chapter structure.</li>
        <li>Suggest changes if needed — the AI will revise.</li>
        <li>When ready, click <strong>Generate Document</strong>.</li>
      </ul>

      <h3>Document Generation</h3>
      <p>After confirming the TOC, the document is generated in the background. The pipeline runs through six stages:</p>
      <ol>
        <li><strong>Capture Intent</strong> — Understanding your requirements</li>
        <li><strong>Generate Brief</strong> — Creating the writing plan</li>
        <li><strong>Create TOC</strong> — Structuring the document</li>
        <li><strong>Index Source</strong> — Gathering reference material from your notes</li>
        <li><strong>Generate Doc</strong> — Writing chapter-by-chapter content</li>
        <li><strong>Refine Doc</strong> — Polishing the final output</li>
      </ol>

      <h3>Checking Status</h3>
      <p>Click <strong>Check Job Status</strong> on the AI Doc Writer tool card, or navigate to <strong>AI Doc Writer Status</strong> from the user menu. The history page shows:</p>
      <ul>
        <li>Document title</li>
        <li>Current step and progress (e.g., "Generate Doc - running (4/6)")</li>
        <li>Completion status (green tag when finished)</li>
        <li>Creation date</li>
      </ul>

      <h3>Viewing and Downloading</h3>
      <p>Once complete:</p>
      <ul>
        <li>Click <strong>View</strong> to read the generated document in a rich text viewer.</li>
        <li>Click <strong>Download</strong> to save it as a Markdown file.</li>
      </ul>

      {/* 8. Collaborative Knowledge Bases */}
      <h2 id="collaborative">8. Collaborative Knowledge Bases</h2>

      <h3>Creating a Team Library</h3>
      <ol>
        <li>Click the <strong>+</strong> button next to the <strong>Team Libraries</strong> header in the sidebar.</li>
        <li>Enter a <strong>KB Name</strong> (required).</li>
        <li>Optionally add a <strong>KB Description</strong>.</li>
        <li>Click <strong>Create Collaborative Knowledge Base</strong>.</li>
      </ol>
      <p>A tip reminds you: "After creating the collaborative KB, you can invite other users to join."</p>

      <h3>Sharing with Team Members</h3>
      <ol>
        <li>Click the dropdown menu (<strong>...</strong>) on your collaborative KB.</li>
        <li>Select <strong>Share</strong> to generate a share link.</li>
        <li>Send the link to your team members.</li>
      </ol>

      <h3>Joining a Shared Knowledge Base</h3>
      <p>When you receive a share link:</p>
      <ol>
        <li>Click the link (you'll be redirected to sign in if needed).</li>
        <li>VNotes verifies the share link and processes your join request.</li>
        <li>On success, you'll see a confirmation: "You have successfully joined the collaborative KB: &#123;name&#125;"</li>
        <li>The page automatically redirects to the dashboard in 3 seconds, or click <strong>Go to Dashboard Now</strong>.</li>
      </ol>

      <h3>Managing a Collaborative KB</h3>
      <p>From the dropdown menu on any collaborative KB:</p>
      <ul>
        <li><strong>New Topic</strong> — Add topics to organize the shared knowledge base.</li>
        <li><strong>Share</strong> — Generate or manage the share link.</li>
        <li><strong>Members</strong> — View all members of the knowledge base.</li>
        <li><strong>Rename</strong> — Update the KB name.</li>
        <li><strong>Leave KB</strong> — Leave the collaborative KB (with confirmation). Other members can continue using it after you leave.</li>
      </ul>

      {/* 9. Membership & Billing */}
      <h2 id="membership">9. Membership &amp; Billing</h2>

      <h3>Plans</h3>
      <p>VNotes offers two tiers:</p>

      <p><strong>Starter — Free</strong></p>
      <ul>
        <li>Clip web pages into your knowledge hub</li>
        <li>Upload and store documents</li>
        <li>Full-text keyword search across all your knowledge cards</li>
        <li>Semantic search — find ideas by meaning, not just keywords</li>
        <li>Edit, favorite, and organize your knowledge cards</li>
        <li>Browse and search by tags and topics</li>
        <li>Share knowledge cards with friends</li>
        <li>10 free LLM assists — try Professional AI advanced features</li>
      </ul>

      <p><strong>Professional — $4.99/month</strong></p>
      <p>Everything in Starter, plus:</p>
      <ul>
        <li>AI-generated summaries for quick understanding</li>
        <li>Automatic categorization by topic</li>
        <li>Ask questions and get answers from your own knowledge hub</li>
        <li>Auto-tagging and topic discovery</li>
        <li>AI Read Assistant</li>
        <li>Ideas Brainstorm with AI</li>
        <li>AI Quiz and Doc generation</li>
        <li>400 LLM assists included each month</li>
      </ul>

      <h3>Upgrading</h3>
      <ol>
        <li>Navigate to the Membership page from the user menu, or click the plan badge next to your name.</li>
        <li>Click <strong>Upgrade to Professional</strong> on the Professional plan card.</li>
        <li>You'll be redirected to a secure Stripe checkout page.</li>
        <li>Complete payment and you're upgraded immediately.</li>
      </ol>

      <h3>Managing Your Subscription</h3>
      <p>On the Membership page, Professional members can:</p>
      <ul>
        <li>View the renewal date and current billing period.</li>
        <li>See the payment method on file (e.g., "Visa ending in 4242").</li>
        <li>Click <strong>Change</strong> to update your payment method via the Stripe customer portal.</li>
        <li>Click <strong>End Membership</strong> to cancel at the end of the current billing period. You'll retain Professional access until then and can <strong>Reactivate</strong> at any time before the period ends.</li>
      </ul>

      <h3>Payment Security</h3>
      <p>All payments are securely processed by Stripe. Your card details are encrypted and never stored on VNotes servers.</p>

      {/* 10. Account Settings */}
      <h2 id="account">10. Account Settings</h2>

      <h3>Viewing Your Profile</h3>
      <p>Click your avatar in the top-right corner and select <strong>Profile</strong> from the dropdown menu. Your profile shows:</p>
      <ul>
        <li><strong>Avatar</strong> — Auto-generated based on your account.</li>
        <li><strong>Username</strong> — Click the edit icon to change it (2–20 characters; letters, numbers, underscores, spaces, and Chinese characters allowed).</li>
        <li><strong>Email</strong> — Display only. Contact an admin if you need to change it.</li>
        <li><strong>Google Account</strong> — Shown if linked.</li>
        <li><strong>Plan Type</strong> — Starter or Professional, with a link to the membership page.</li>
      </ul>

      <h3>Usage Statistics</h3>
      <p>Your profile displays three stats:</p>
      <ul>
        <li><strong>Days Used</strong> — How long you've been on VNotes.</li>
        <li><strong>Notes</strong> — Total notes in your account.</li>
        <li><strong>Favorites</strong> — Number of pinned notes.</li>
      </ul>

      <h3>User Menu</h3>
      <p>Click your avatar to access:</p>
      <ul>
        <li><strong>Profile</strong> — View and edit your profile.</li>
        <li><strong>AI Doc Writer Status</strong> — Check document generation history.</li>
        <li><strong>Membership</strong> — Manage your plan and billing.</li>
        <li><strong>Logout</strong> — Sign out of your account.</li>
      </ul>

      {/* 11. Chrome Extension */}
      <h2 id="chrome-extension">11. Chrome Extension — VNotes AI Clip</h2>

      <h3>What is VNotes AI Clip?</h3>
      <p>VNotes AI Clip is a browser extension that saves web articles to your personal knowledge base with one click. It extracts the article content, title, author, and publication date, then uses AI to automatically generate summaries and tags for easy retrieval later.</p>

      <h3>Installation</h3>
      <ol>
        <li>Install VNotes AI Clip from the <a href="https://chromewebstore.google.com" target="_blank" rel="noopener noreferrer">Chrome Web Store</a>.</li>
        <li>Pin the extension by clicking the puzzle icon in Chrome's toolbar and pinning <strong>VNotes AI Clip</strong> for quick access.</li>
      </ol>

      <h3>Sign In</h3>
      <p>Click the VNotes AI Clip icon in your toolbar to open the popup.</p>
      <p><strong>Option A — Email &amp; Password:</strong><br />
        Enter your VNotes account email and password, then click <strong>Sign In</strong>.</p>
      <p><strong>Option B — Google:</strong><br />
        Click <strong>Continue with Google</strong> and follow the prompts.</p>
      <p><strong>New user?</strong> Click <strong>Create an account</strong> to register.</p>

      <h3>Saving a Web Page</h3>
      <ol>
        <li>Open the article or page you want to save.</li>
        <li>Click the <strong>VNotes AI Clip</strong> icon in your toolbar.</li>
        <li>The popup shows the current page title and URL.</li>
        <li>Choose a <strong>Space</strong> from the list — this is the knowledge base folder where your clip will be saved.</li>
        <li>Click the Space to start saving.</li>
        <li>Wait for the confirmation checkmark to appear.</li>
      </ol>
      <p>That's it — your clip is saved with AI-generated summaries and tags.</p>

      <h3>Managing Your Clips</h3>
      <p>All saved clips are stored in your VNotes knowledge base. Visit <a href="https://web.vnotes.ai" target="_blank" rel="noopener noreferrer">web.vnotes.ai</a> to:</p>
      <ul>
        <li>Browse clips organized by Space</li>
        <li>Read AI-generated summaries</li>
        <li>Search across all your clips using natural language</li>
        <li>Edit, tag, or delete saved clips</li>
      </ul>

      <h3>Tips</h3>
      <ul>
        <li><strong>Wait for the page to fully load</strong> before clicking the extension icon for best extraction results.</li>
        <li><strong>Log in to paywalled sites first</strong> — the extension can only extract content you can see.</li>
        <li><strong>Use different Spaces</strong> to organize clips by project, topic, or category.</li>
        <li><strong>Some sites have limited support</strong> — social media feeds, video platforms, and banking sites will only capture the page title and URL.</li>
      </ul>

      <h3>Signing Out</h3>
      <p>Click the logout icon (arrow) in the top-right corner of the popup to sign out. Your saved clips remain safe on VNotes.</p>

      <h3>Uninstalling</h3>
      <p>Right-click the extension icon → <strong>Remove from Chrome</strong>. Your saved clips on VNotes are not affected.</p>

      {/* 12. Need Help? */}
      <h2 id="need-help">12. Need Help?</h2>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:dev@vnotes.ai">dev@vnotes.ai</a></li>
        <li><strong>Website:</strong> <a href="https://vnotes.ai" target="_blank" rel="noopener noreferrer">vnotes.ai</a></li>
      </ul>

      <p style={{ marginTop: 48, textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
        VNotes AI — Your Personal Knowledge Assistant
      </p>
    </div>
  );
};

export default Guide;
